// Bridge between the live collab client (BlueprintEditor hook) and the
// non-React graph-state modules (ENG-539 Stage 2 — editor path).
//
// Lets the editor ask "is co-editing live?" and route a blueprint edit into
// the shared Yjs doc, and lets it receive remote blueprint changes — without
// either side importing the other. A module singleton because the collab
// client is owned by a React hook while the editor state is a plain module.

let _client = null        // the connected CollabBlueprintClient (or null)
let _unsub = null        // active observeBlueprint unsubscribe
let _unsubSync = null    // active onSyncChange unsubscribe
let _onRemote = null     // handler set by BlueprintEditor
let _onSync = null       // sync state change callbacks Set

function rewire() {
  if (_unsub) { _unsub(); _unsub = null }
  if (_client && _client.isSynced() && _onRemote) {
    _unsub = _client.observeBlueprint((blueprint) => {
      _onRemote(blueprint)
    })
  }
}

function rewireSync() {
  if (_unsubSync) { _unsubSync(); _unsubSync = null }
  if (_client && _onSync && _onSync.size > 0) {
    _unsubSync = _client.onSyncChange((synced) => {
      // When client transitions to synced, attach the remote observer.
      // rewire() safely cleans up any prior observer before re-attaching.
      if (synced) {
        rewire()
      }
      // Notify sync-state subscribers
      _onSync.forEach((cb) => {
        try {
          cb(synced)
        } catch (err) {
          console.error('onCollabSyncChange callback error:', err)
        }
      })
    })
  }
}

function emitSync() {
  if (_onSync && _onSync.size > 0) {
    const synced = !!(_client && _client.isSynced())
    _onSync.forEach((cb) => {
      try {
        cb(synced)
      } catch (err) {
        console.error('onCollabSyncChange callback error:', err)
      }
    })
  }
}

// registerCollabBlueprintClient — called by BlueprintEditor when a blueprint
// client connects. Pass null to disconnect. Establishes the observation link.
export function registerCollabBlueprintClient(client) {
  _client = client
  rewire()
  rewireSync()
  emitSync()
}

// setRemoteBlueprintHandler — BlueprintEditor registers how to apply a remote
// blueprint change (hydrate editor state + re-render).
export function setRemoteBlueprintHandler(cb) {
  _onRemote = cb
  rewire()
}

// onCollabSyncChange — subscribe to sync/connection-state changes. Fires when
// the client connects/disconnects or when sync state changes, so the editor
// can recompute read-only status. Returns an unsubscribe function.
export function onCollabSyncChange(cb) {
  if (!_onSync) {
    _onSync = new Set()
  }
  _onSync.add(cb)
  rewireSync()
  // Prime with current state
  emitSync()
  return () => {
    _onSync.delete(cb)
    if (_onSync.size === 0) {
      if (_unsubSync) { _unsubSync(); _unsubSync = null }
    }
  }
}

// isBlueprintCollabActive — true only when a client is connected AND synced,
// so a caller can safely route an edit through the doc (else fall back to
// localStorage or direct state mutation).
export function isBlueprintCollabActive() {
  return !!(_client && _client.isSynced())
}

// writeBlueprintCollab — route an edit into the shared doc. Returns false when
// co-editing isn't live, so the caller falls back to localStorage/direct state.
export function writeBlueprintCollab(graphs) {
  if (!isBlueprintCollabActive()) return false
  _client.writeBlueprint(graphs)
  return true
}

// currentCollabBlueprint — the live converged blueprint when co-editing is
// active, else null. Lets the editor prefer the live doc over a stale
// localStorage or in-memory snapshot.
export function currentCollabBlueprint() {
  if (!isBlueprintCollabActive()) return null
  return _client.readBlueprint()
}

// flushCollabBlueprint — trigger synchronous persist (for Plan C checkpoint).
export async function flushCollabBlueprint() {
  if (!_client) return Promise.resolve()
  return _client.flush()
}
